import { useState, useRef, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AiVectorChart from './AiVectorChart';

/**
 * Test surface for the vector (RAG) chatbot — POST /ai-vector/chat.
 *
 * Deliberately separate from AiSupportChat.jsx so the existing keyword chatbot
 * is untouched while both are compared side by side. Stateless: conversation
 * history is held here and posted with each request.
 */

const SAMPLE_QUESTIONS = [
  'anyone working at height without fall protection',
  'vehicles that failed a brake or tire check',
  'workers who refused to continue a job on safety grounds',
  'show a breakdown of forms by type',
  'chart the forms submitted per month',
];

const AiVectorChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [indexStatus, setIndexStatus] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    api
      .get('/ai-vector/index/status')
      .then((res) => setIndexStatus(res))
      .catch(() => setIndexStatus(null));
  }, []);

  const send = async (question) => {
    const text = (question ?? input).trim();
    if (!text || loading) return;

    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/ai-vector/chat', { question: text, history });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: res?.answer || 'No response received.',
          chart: res?.chart || null,
          sources: res?.sources || [],
          filters: res?.filters_applied || null,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: `Request failed: ${err?.message || err?.detail || 'unknown error'}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        AI Vector Chat (test)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Semantic search over safety forms.
        {indexStatus
          ? ` Index: ${indexStatus.total_chunks} chunks.`
          : ' Index status unavailable.'}
      </Typography>

      {messages.length === 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {SAMPLE_QUESTIONS.map((q) => (
            <Chip key={q} label={q} size="small" variant="outlined" onClick={() => send(q)} />
          ))}
        </Stack>
      )}

      <Stack spacing={2}>
        {messages.map((m, i) => (
          <Box
            key={i}
            sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: m.role === 'user' ? '80%' : '100%',
              width: m.role === 'user' ? 'auto' : '100%',
              bgcolor: m.role === 'user' ? 'primary.main' : 'background.paper',
              color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
              border: m.role === 'user' ? 'none' : '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {m.text}
            </Typography>

            {m.chart && <AiVectorChart spec={m.chart} mode="light" />}

            {m.filters && (m.filters.form_type || m.filters.date_range || m.filters.relaxed) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Filters — form: {m.filters.form_type || 'any'}
                {m.filters.date_range ? `, dates: ${m.filters.date_range.join('–')}` : ''}
                {m.filters.relaxed ? ' (relaxed — no exact match on filters)' : ''}
              </Typography>
            )}

            {m.sources?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Sources:
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {m.sources.slice(0, 6).map((s, j) => (
                    <Chip
                      key={j}
                      size="small"
                      variant="outlined"
                      label={`${s.form_type} #${s.record_id} · ${s.similarity}`}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching…
            </Typography>
          </Box>
        )}
        <div ref={bottomRef} />
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about safety forms…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button variant="contained" onClick={() => send()} disabled={loading || !input.trim()}>
          <SendIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export default AiVectorChat;
